import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { cn } from '@/shared/lib'
import { IconButton } from '@/shared/ui/icon-button'

interface SlideOverProps {
  open: boolean
  title: string
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  children: ReactNode
}

// Плавающая правая панель формы (референс: create-task поверх контента).
export function SlideOver({
  open,
  title,
  onClose,
  footer,
  wide = false,
  children,
}: SlideOverProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // Фокус — только в момент открытия. onClose в депсах быть не должно: его identity
  // меняется на каждом ререндере родителя (инлайн-стрелки/замыкания на стейт формы),
  // и повторный прогон эффекта уводил бы фокус с инпута на панель при каждом вводе.
  useEffect(() => {
    if (open) panelRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Закрыть панель"
        onClick={onClose}
        className="absolute inset-0 size-full cursor-default bg-app-bg/70 backdrop-blur-[2px]"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'absolute bottom-3 right-3 top-3 flex flex-col overflow-hidden rounded-panel border border-divider bg-panel shadow-2xl outline-none',
          wide ? 'w-[560px]' : 'w-[440px]',
          'max-w-[calc(100vw-1.5rem)]',
        )}
      >
        <header className="flex items-center justify-between gap-3 px-6 pb-2 pt-5">
          <h2 className="text-title font-medium">{title}</h2>
          <IconButton icon="x" aria-label="Закрыть" variant="raise" size="sm" onClick={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-divider px-6 py-4">
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
